import { Col, Row } from "@/components";
import { ArrowLink } from "@/components/ui/ArrowLink";
import { ExternalLink } from "@/icons";
import { RichText } from "@/sections";
import { HeadingLevel } from "@/types/headings";
import clsx from "clsx";
import { ComponentType, MouseEvent, ReactNode } from "react";
import "./CardCategory.scss";

type Image = {
  alt: string;
  src: string;
  title: string;
};

type CardCategoryProps = {
  image?: Image;
  title: string;
  titleTag?: HeadingLevel;
  href?: string;
  description?: string | ReactNode;
  description2?: string | ReactNode;
  isExternal?: boolean;
  showIcon?: boolean;
  goToLabel?: string;
  asImage?: "img";
  asLink?: "a" | ComponentType<any>;
  className?: string;
  classNameTitle?: string;
  classNameDescription?: string;
  classNameDescriptionTitle?: string;
  maxLinesTitle?: number;
  maxLinesDescription?: number;
  handleOnClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
};

export const CardCategory = ({
  image,
  title,
  titleTag: TitleTag = "h1",
  href,
  description,
  description2,
  isExternal = false,
  showIcon = false,
  goToLabel,
  asLink = "a",
  asImage = "img",
  className,
  classNameTitle,
  classNameDescription,
  classNameDescriptionTitle,
  maxLinesTitle = undefined,
  maxLinesDescription = undefined,
  handleOnClick,
}: CardCategoryProps) => {
  const ImageComponent = asImage;
  const LinkComponent = asLink;
  return (
    <LinkComponent
      className={clsx(
        "ui-card-category shadow br-x4 bg-white",
        className,
        handleOnClick && "cursor-pointer",
      )}
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noreferrer noopener" : undefined}
      onClick={handleOnClick ?? undefined}
    >
      {image?.src && (
        <header className="ui-card-category-header">
          <ImageComponent
            src={image?.src}
            alt={image?.alt}
            title={image?.title}
          />
        </header>
      )}

      <Col
        className={clsx(
          image?.src
            ? "ui-card-category-content-with-image ui-mt-negative"
            : "ui-card-category-content",
          "gap-x8 br-x4 bg-white",
        )}
      >
        <Row alignItems={"center"} justifyContent={"space-between"} gap={"x4"}>
          <TitleTag
            className={clsx(
              "flex-1 cursor-pointer hover-underline",
              classNameTitle
                ? classNameTitle
                : "text-primary fs-5 md-fs-custom-2",
              maxLinesTitle && "clamped",
            )}
            style={{ "--max-lines": maxLinesTitle } as React.CSSProperties}
            title={maxLinesTitle ? title : undefined}
          >
            {title}
            {isExternal && (
              <span className="visually-hidden">
                (si apre in una nuova finestra)
              </span>
            )}
          </TitleTag>

          {showIcon && (
            <span>
              <ExternalLink
                width={24}
                height={24}
                color="primary"
                className="ui-external-link"
              />
            </span>
          )}
        </Row>
        <div
          className={clsx(
            classNameDescription,
            !maxLinesDescription &&
            classNameDescription?.includes("max-h-") &&
            "pb-x24",
          )}
        >
          <div
            className={clsx(
              classNameDescriptionTitle
                ? classNameDescriptionTitle
                : "fs-6 md-fs-custom text-slate-20",
            )}
          >
            <RichText
              description={description}
              maxLines={maxLinesDescription}
              useShowToggle={false}
            />
          </div>
          <div className="fs-6 md-fs-custom fw-regular text-slate-28">
            <RichText description={description2} />
          </div>
        </div>
      </Col>
      {goToLabel && href && (
        <footer className="ui-card-category-footer bg-white">
          <ArrowLink href={href ?? "#"} isExternal={isExternal}>
            {goToLabel?.toUpperCase()}
          </ArrowLink>
        </footer>
      )}
    </LinkComponent>
  );
};
